/**
 * pdfSections.js — Sections de contenu du rapport PDF (annotations,
 * recommandations, observations vétérinaires, footers).
 *
 * Extrait de pdfHelpers.js (refacto structurelle 1.4b-0, cap 500 lignes).
 * Aucune i18n, aucun changement de comportement. BRAND importé depuis ./pdfHelpers.
 */
import i18n from '../i18n';
import { BRAND, checkPageBreak, drawSectionTitle } from './pdfHelpers';
import { formatDate, formatDateLocale, formatNumberLocale } from '../utils/formatters';
import { resolveAnnotationLabel } from '../constants/activityTypes';
import { pdfText } from './pdfPeriod';

/**
 * Liste des annotations (« • 11:00 -> 11:30 : Libellé », notes en gris).
 * Libellé dérivé de ann.type (bascule E1, comme à l'écran).
 */
export function drawAnnotationsList(pdf, annotations, yPos, margin, pageHeight) {
  yPos = checkPageBreak(pdf, yPos, 30, margin, pageHeight);
  yPos = drawSectionTitle(pdf, i18n.t('session.annotations.listTitle'), yPos, margin);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  annotations.forEach(ann => {
    const time = ann.startTime || i18n.t('common.notProvided');
    const end  = ann.endTime ? ` → ${ann.endTime}` : '';
    pdf.setTextColor(...BRAND.dark);
    pdf.text(pdfText(`• ${time}${end} : ${resolveAnnotationLabel(ann, i18n.t)}`), margin + 3, yPos);
    if (ann.notes) {
      pdf.setTextColor(150, 150, 150);
      pdf.text(`  ${ann.notes}`, margin + 8, yPos + 4);
      yPos += 4;
    }
    yPos += 6;
  });
  return yPos + 3;
}

/**
 * Section « Recommandations » : affichée seulement jusqu'à 48 h. Au-delà, la
 * moyenne masque la variabilité ; P95, % de dépassement et AUC sont plus
 * pertinents. Durée lue sur la session reçue (fenêtrée avec une période).
 */
export function drawRecommendationsSection(pdf, session, readings, yPos, margin, pageWidth, pageHeight) {
  yPos = checkPageBreak(pdf, yPos, 40, margin, pageHeight);
  const durationH = (() => {
    if (session.duration_min) return session.duration_min / 60;
    // Fallback : calculer depuis les readings
    if (readings && readings.length >= 2) {
      const t0 = new Date(readings[0].timestamp).getTime();
      const t1 = new Date(readings[readings.length - 1].timestamp).getTime();
      return (t1 - t0) / 3_600_000;
    }
    return 0;
  })();

  yPos = drawSectionTitle(pdf, i18n.t('pdf.section.recommendations'), yPos, margin);
  if (durationH <= 48) {
    yPos = drawRecommendations(pdf, generateRecommendations(session), yPos, margin, pageWidth);
    // Avertissement systématique
    pdf.setFontSize(7.5);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(107, 114, 128);
    pdf.text(i18n.t('pdf.recommendations.disclaimer'), margin, yPos + 3);
    yPos += 12;
  } else {
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(107, 114, 128);
    const longMsg = i18n.t('pdf.recommendations.longSession', { returnObjects: true });
    longMsg.forEach((line, i) => {
      pdf.text(line, margin, yPos + i * 6);
    });
    yPos += 24;
  }
  return yPos + 5;
}

/**
 * Génère les recommandations automatiques basées sur les données
 */
export function generateRecommendations(session) {
  const recs = [];

  // PM2.5
  if (session.pm2_5_avg > 40) {
    recs.push({ level: 'danger', text: i18n.t('pdf.recommendations.rec.pm25High', { value: formatNumberLocale(session.pm2_5_avg, 1) }) });
  } else if (session.pm2_5_avg > 25) {
    recs.push({ level: 'warning', text: i18n.t('pdf.recommendations.rec.pm25Moderate', { value: formatNumberLocale(session.pm2_5_avg, 1) }) });
  } else if (session.pm2_5_avg != null) {
    recs.push({ level: 'good', text: i18n.t('pdf.recommendations.rec.pm25Good', { value: formatNumberLocale(session.pm2_5_avg, 1) }) });
  }

  // PM10
  if (session.pm10_avg > 80) {
    recs.push({ level: 'danger', text: i18n.t('pdf.recommendations.rec.pm10High', { value: formatNumberLocale(session.pm10_avg, 1) }) });
  } else if (session.pm10_avg > 50) {
    recs.push({ level: 'warning', text: i18n.t('pdf.recommendations.rec.pm10Moderate', { value: formatNumberLocale(session.pm10_avg, 1) }) });
  }

  // Pics PM
  if (session.pm2_5_max > 100) {
    recs.push({ level: 'warning', text: i18n.t('pdf.recommendations.rec.pm25Peak', { max: formatNumberLocale(session.pm2_5_max, 1) }) });
  }

  // Température
  if (session.temp_avg != null && (session.temp_avg < 5 || session.temp_avg > 25)) {
    recs.push({ level: 'warning', text: i18n.t('pdf.recommendations.rec.tempOut', { value: formatNumberLocale(session.temp_avg, 1) }) });
  }

  // Humidité
  if (session.humidity_avg != null && session.humidity_avg > 70) {
    recs.push({ level: 'warning', text: i18n.t('pdf.recommendations.rec.humidityHigh', { value: formatNumberLocale(session.humidity_avg, 0) }) });
  } else if (session.humidity_avg != null && session.humidity_avg < 40) {
    recs.push({ level: 'warning', text: i18n.t('pdf.recommendations.rec.humidityLow', { value: formatNumberLocale(session.humidity_avg, 0) }) });
  }

  if (recs.length === 0) {
    recs.push({ level: 'good', text: i18n.t('pdf.recommendations.rec.allNormal') });
  }

  return recs;
}

/**
 * Dessine les recommandations dans le PDF
 */
export function drawRecommendations(pdf, recommendations, yPos, margin, pageWidth) {
  const contentWidth = pageWidth - 2 * margin;

  recommendations.forEach(rec => {
    const colors = {
      good: { bg: [232, 245, 233], border: BRAND.green, icon: 'OK' },
      warning: { bg: [255, 243, 224], border: BRAND.orange, icon: '!' },
      danger: { bg: [255, 235, 238], border: BRAND.red, icon: 'X' }
    };
    const c = colors[rec.level] || colors.warning;

    // Calculer la hauteur du texte
    const lines = pdf.splitTextToSize(rec.text, contentWidth - 14);
    const boxHeight = lines.length * 4.5 + 6;

    // Fond
    pdf.setFillColor(...c.bg);
    pdf.rect(margin, yPos - 4, contentWidth, boxHeight, 'F');

    // Bordure gauche
    pdf.setFillColor(...c.border);
    pdf.rect(margin, yPos - 4, 3, boxHeight, 'F');

    // Texte
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...BRAND.dark);
    pdf.text(lines, margin + 7, yPos + 1);

    yPos += boxHeight + 4;
  });

  return yPos;
}

/**
 * Dessine les observations vétérinaires dans le PDF
 */
export function drawVetNotes(pdf, vetNotes, yPos, margin, pageWidth) {
  if (!vetNotes || !vetNotes.content) return yPos;

  const contentWidth = pageWidth - margin * 2;

  // Titre section
  pdf.setFillColor(16, 185, 129); // vert
  pdf.rect(margin, yPos, contentWidth, 8, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.text(i18n.t('pdf.section.vetNotes'), margin + 4, yPos + 5.5);
  yPos += 12;

  // Auteur et date
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(8);
  pdf.setTextColor(100, 100, 100);
  const dateStr = vetNotes.updatedAt ? formatDateLocale(vetNotes.updatedAt, { day: 'numeric', month: 'long', year: 'numeric' }) : '';
  pdf.text(`${vetNotes.authorName || i18n.t('pdf.vetNotes.authorFallback')} · ${dateStr}`, margin + 4, yPos);
  yPos += 6;

  // Contenu
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...BRAND.dark);
  const lines = pdf.splitTextToSize(vetNotes.content, contentWidth - 8);

  // Cadre vert clair
  const boxHeight = lines.length * 4.5 + 6;
  pdf.setFillColor(236, 253, 245); // vert très clair
  pdf.setDrawColor(16, 185, 129);
  pdf.setLineWidth(0.5);
  pdf.roundedRect(margin, yPos, contentWidth, boxHeight, 2, 2, 'FD');

  pdf.setTextColor(...BRAND.dark);
  pdf.text(lines, margin + 4, yPos + 5);
  yPos += boxHeight + 6;

  return yPos;
}

/**
 * Dessine le footer sur chaque page
 * @param {string} [mention] mention complémentaire (PDF de comparaison), sur sa
 *   propre ligne entre le filet et la ligne existante ; absente : footer
 *   strictement identique au PDF de session.
 */
export function drawFooters(pdf, pageWidth, pageHeight, mention) {
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);

    // Ligne de séparation
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.line(15, pageHeight - 15, pageWidth - 15, pageHeight - 15);

    if (mention) {
      pdf.setFontSize(7);
      pdf.setTextColor(150, 150, 150);
      pdf.setFont('helvetica', 'normal');
      pdf.text(mention, 15, pageHeight - 11.5);
    }

    // Texte footer
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.setFont('helvetica', 'normal');
    pdf.text(
      'EkynAir · ekynair.app',
      15,
      pageHeight - 8
    );
    pdf.text(
      i18n.t('pdf.footer.page', { i, total: totalPages, date: formatDate(new Date(), false) }),
      pageWidth - 15,
      pageHeight - 8,
      { align: 'right' }
    );
  }
}
